import React from 'react';

import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';

import { RADIUS } from 'src/theme/ui-tokens';


export const FILTER_CHIP_GRID_2_COL_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 1,
  width: '100%',
};

export const FILTER_CHIP_GRID_3_COL_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 1,
  width: '100%',
};

export const FILTER_CHIP_GRID_CELL_SX = {
  display: 'flex',
  width: '100%',
  minWidth: 0,
};

function formatChipCount(count: unknown) {
  const n = Number(count);
  if (Number.isFinite(n)) return n.toLocaleString('ko-KR');
  return String(count);
}

/**
 * 필터 칩 — 라벨(좌) · 문서 수(우) 가로 배치.
 *
 * ⚠️ 아래 `@param` 목록은 계약이다 — 지우면 `.tsx` 호출부에서 **기본값 없는 prop이
 * 전부 필수**로 추론된다.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.label]
 * @param {boolean} [props.isSelected]
 * @param {Function} [props.onClick]
 * @param {Object} [props.sx]
 * @param {boolean} [props.clickable]
 * @param {boolean} [props.stopPropagation]
 * @param {number|string} [props.count]  `showCount`일 때만 그린다
 * @param {boolean} [props.showCount]
 * @param {boolean} [props.fullWidth]
 * @param {string} [props.variant]
 */
function FilterChip({
  label,
  isSelected = false,
  onClick,
  sx,
  clickable = true,
  stopPropagation = true,
  count,
  showCount = false,
  fullWidth = false,
  variant = 'contained',
  ...rest
}: {
  label?: React.ReactNode
  isSelected?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  sx?: Record<string, unknown>
  clickable?: boolean
  stopPropagation?: boolean
  count?: number | string
  showCount?: boolean
  fullWidth?: boolean
  variant?: string
} & Record<string, unknown>) {
  const theme = useTheme();
  const isSoft = variant === 'soft';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (onClick) onClick(event);
  };

  /**
   * ⚠️ **카운트를 숨기려면 `showCount`를 끄는 것으로는 부족하다** — `count`를
   * `undefined`로 줘야 한다. `showCount`는 "값이 0이어도 숫자로 그린다"는 뜻이고,
   * 무엇을 그릴지는 `count`가 있느냐가 정한다. 비활성 칩에 `count: undefined`를 넘겨
   * 숨기는 것이 정상 용법이다.
   */
  const hasCount = showCount || (count !== undefined && count !== null);
  const displayCount = showCount ? (Number.isFinite(Number(count)) ? Number(count) : 0) : count;

  const selectedBg = isSoft
    ? alpha(theme.palette.primary.main, 0.16)
    : theme.palette.primary.main;
  const selectedColor = isSoft
    ? theme.palette.primary.dark
    : theme.palette.primary.contrastText;
  const selectedHoverBg = isSoft
    ? alpha(theme.palette.primary.main, 0.32)
    : theme.palette.primary.dark;
  const selectedCountColor = isSoft
    ? theme.palette.primary.dark
    : theme.palette.primary.contrastText;

  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? handleClick : undefined}
      {...rest}
      sx={{
        height: 30,
        width: fullWidth ? '100%' : 'auto',
        minWidth: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: hasCount ? 'space-between' : 'center',
        gap: 1,
        px: 1.5,
        py: 0,
        m: 0,
        borderRadius: RADIUS.md,
        border: '1px solid',
        borderColor: isSelected ? 'transparent' : theme.palette.grey[300],
        bgcolor: isSelected ? selectedBg : theme.palette.background.paper,
        color: isSelected ? selectedColor : theme.palette.text.primary,
        cursor: clickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        textAlign: 'left',
        boxSizing: 'border-box',
        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        '&:hover': clickable ? {
          bgcolor: isSelected ? selectedHoverBg : theme.palette.grey[50],
          borderColor: isSelected ? 'transparent' : theme.palette.grey[400],
        } : undefined,
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        ...sx,
      }}
    >
      <Box
        component="span"
        sx={{
          flex: hasCount ? '1 1 auto' : undefined,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          typography: 'uiBody',
          fontWeight: isSelected ? 600 : 500,
          lineHeight: 1.25,
          textAlign: hasCount ? 'left' : 'center',
        }}
      >
        {label}
      </Box>
      {hasCount && (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            typography: 'uiMeta',
            lineHeight: 1.25,
            color: isSelected ? selectedCountColor : theme.palette.text.secondary,
            opacity: isSelected ? 0.92 : 1,
          }}
        >
          {formatChipCount(displayCount)}
        </Box>
      )}
    </Box>
  );
}

export default FilterChip;
